import cn from 'classnames';
import React from 'react';
import { Typography, Tag, Divider, Button } from 'antd';
// Types
import {
  CBDeliverableItemType,
  CbDeliverableItemV2Status,
  CbDeliverableV2Status,
} from 'api/graphql-global-types';
import {
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2,
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableCopyV2,
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableFileItemV2,
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableFilesItemV2,
  GetCampaignOffers_getCampaignOffers_entities_submissions,
} from 'api/campaignV2/types/GetCampaignOffers';
// Constants
import { POST_DELIVERABLE_LINK_TYPES } from 'constants/deliverables';
// Helpers
import {
  getSeparatedDeliverableItems,
  getSubmissionInfo,
} from 'helpers/deliverables';
// Components
import DropdownItem from '../DropdownItem/DropdownItem';
import ReplaceSubmissionLink from '../ReplaceSubmissionLink/ReplaceSubmissionLink';
// Styles
import styles from './DeliverableSubmitted.module.scss';

const { Text } = Typography;

type DeliverableType = 'content' | 'post';

type DeliverableSubmittedProps = {
  deliverable: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2;
  submissions?:
    | GetCampaignOffers_getCampaignOffers_entities_submissions[]
    | null;
  defaultExpanded?: boolean;
  className?: string;
  deliverableType?: DeliverableType;
  // Extension request props
  hasPendingRequest?: (deliverableId: string) => boolean;
  onRequestExtension?: (
    deliverable: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2
  ) => void;
  onReviewExtension?: (
    deliverable: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2
  ) => void;
};

const StatusMap: Partial<
  Record<CbDeliverableItemV2Status, { label: string; color: string }>
> = {
  [CbDeliverableItemV2Status.APPROVED]: {
    label: 'Approved',
    color: 'green',
  },
  [CbDeliverableItemV2Status.ON_BRAND_REVIEW]: {
    label: 'In Review',
    color: 'blue',
  },
};

type MediaItem =
  | GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableFileItemV2
  | GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableFilesItemV2;

const isFilesMediaItem = (
  item: MediaItem
): item is GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableFilesItemV2 =>
  item.type === CBDeliverableItemType.FILES;

const getMediaItemName = (item: MediaItem): string | undefined => {
  if (isFilesMediaItem(item)) {
    return item.files[0]?.name || item.files[0]?.key;
  }

  return item.name || item.key;
};

const getMediaItemUrl = (item: MediaItem): string | undefined => {
  if (isFilesMediaItem(item)) {
    return item.files[0]?.originalUrl || item.files[0]?.url || undefined;
  }

  return item.originalUrl || item.url || undefined;
};

const DeliverableSubmitted: React.FC<DeliverableSubmittedProps> = ({
  deliverable,
  submissions,
  className,
  defaultExpanded = true,
  deliverableType = 'content',
  hasPendingRequest,
  onRequestExtension,
  onReviewExtension,
}) => {
  let files: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableFileItemV2[] =
    [];
  let filesItems: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableFilesItemV2[] =
    [];
  let copies: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableCopyV2[] =
    [];
  let deliverableSubmissions: GetCampaignOffers_getCampaignOffers_entities_submissions[] =
    [];

  // If no submissions, then its used in content card
  if (!submissions) {
    const {
      files: defaultFiles,
      filesItems: defaultFilesItems,
      copies: defaultCopies,
    } = getSeparatedDeliverableItems(
      deliverable as GetCampaignOffers_getCampaignOffers_entities_deliverablesV2
    );
    files =
      defaultFiles as GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableFileItemV2[];
    filesItems =
      defaultFilesItems as GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableFilesItemV2[];
    copies =
      defaultCopies as GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items_CbDeliverableCopyV2[];
  } else {
    deliverableSubmissions = submissions.filter(
      (submission) => submission.task?.id === deliverable?.task?.id
    );
  }

  const isLinkType = POST_DELIVERABLE_LINK_TYPES.includes(
    deliverableSubmissions[0]?.type ?? ''
  );

  // Determine if extension buttons should be shown
  // Should show posts that are not published as well as content that are not published or under review
  const shouldShowExtensionButtons =
    (deliverableType === 'post' &&
      deliverable?.status === CbDeliverableV2Status.ON_BRAND_REVIEW) ||
    (deliverable?.status !== CbDeliverableV2Status.ON_BRAND_REVIEW &&
      deliverable?.status !== CbDeliverableV2Status.PUBLISHED);

  const mediaItems: MediaItem[] = [...files, ...filesItems].sort((a, b) => {
    const statusPriority = {
      [CbDeliverableItemV2Status.APPROVED]: 0,
      [CbDeliverableItemV2Status.ON_BRAND_REVIEW]: 1,
      [CbDeliverableItemV2Status.REJECTED]: 2,
      [CbDeliverableItemV2Status.NOT_SUBMITTED]: 3,
      [CbDeliverableItemV2Status.ARCHIVED]: 4,
    };

    const aPriority = statusPriority[a.status] ?? 999;
    const bPriority = statusPriority[b.status] ?? 999;

    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }

    return (a.task?.orderIdx ?? 0) - (b.task?.orderIdx ?? 0);
  });

  return (
    <div className={cn(styles.root, className)}>
      <DropdownItem
        label="Submitted Content"
        labelClassName={styles.label}
        defaultExpanded={defaultExpanded}
      >
        <div className={styles.content}>
          {!submissions && <Text>Uploaded Media:</Text>}
          {mediaItems.map((item) => {
            const index = item?.task?.orderIdx ?? 0;
            const labelInfo = StatusMap[item?.status ?? ''] || {
              label: 'Unknown',
              color: 'default',
            };
            const isFinalContent = isFilesMediaItem(item);
            const fileName = getMediaItemName(item);
            const fileUrl = getMediaItemUrl(item);

            return (
              <div className={styles.fileWrapper} key={item.id}>
                <div className={styles.title}>
                  <div className={styles.titleText}>
                    <Text>
                      {isFinalContent
                        ? `Final Content - ${item?.task?.title}`
                        : `Content ${index + 1} - ${item?.task?.title}`}
                    </Text>
                  </div>
                  {labelInfo && (
                    <Tag color={labelInfo.color}>{labelInfo.label}</Tag>
                  )}
                </div>
                <div className={styles.tags}>
                  <Tag>
                    <a
                      href={fileUrl || undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {fileName}
                    </a>
                  </Tag>
                </div>
                <Divider className={styles.divider} />
              </div>
            );
          })}
          {/* Copies */}
          {copies
            .filter((item) => item.type === CBDeliverableItemType.COPY)
            .sort((a, b) => {
              // First, sort by submission status - submitted items go to top
              const aIsSubmitted =
                a.status === CbDeliverableItemV2Status.ON_BRAND_REVIEW ||
                a.status === CbDeliverableItemV2Status.APPROVED;
              const bIsSubmitted =
                b.status === CbDeliverableItemV2Status.ON_BRAND_REVIEW ||
                b.status === CbDeliverableItemV2Status.APPROVED;

              if (aIsSubmitted !== bIsSubmitted) {
                return aIsSubmitted ? -1 : 1;
              }

              // Then sort by orderIdx
              return (a.task?.orderIdx ?? 0) - (b.task?.orderIdx ?? 0);
            })
            .map((copy) => {
              const labelInfo = StatusMap[copy?.status ?? ''];
              return (
                <div className={styles.copyWrapper} key={copy.id}>
                  <div className={styles.title}>
                    <Text>Post caption:</Text>
                    {labelInfo && (
                      <Tag color={labelInfo.color}>{labelInfo.label}</Tag>
                    )}
                  </div>
                  <Text>{copy.copy}</Text>
                </div>
              );
            })}
          {/* Submissions */}
          {submissions && (
            <>
              <Text>
                {isLinkType ? 'Submitted URL Link:' : 'Uploaded Media:'}
              </Text>
              {isLinkType ? (
                <div className={styles.linkRow}>
                  <Text>
                    <a
                      href={(deliverableSubmissions[0] as any)?.link || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.link}
                    >
                      {(deliverableSubmissions[0] as any)?.link}
                    </a>
                  </Text>
                  {deliverableSubmissions[0]?.id && (
                    <ReplaceSubmissionLink
                      submissionId={deliverableSubmissions[0].id}
                      currentLink={(deliverableSubmissions[0] as any)?.link}
                    />
                  )}
                </div>
              ) : (
                <div className={styles.tags}>
                  {deliverableSubmissions?.map((submission) => {
                    const info = getSubmissionInfo(submission);
                    return (
                      <Tag key={submission.id}>
                        <a
                          href={info.link || undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {info.label}
                        </a>
                      </Tag>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* Extension Request Buttons */}
          {shouldShowExtensionButtons &&
            hasPendingRequest &&
            onRequestExtension &&
            onReviewExtension && (
              <div className={styles.extensionButtons}>
                {hasPendingRequest(deliverable.id) ? (
                  <Button
                    type="primary"
                    onClick={() => onReviewExtension(deliverable)}
                  >
                    Review Extension
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    onClick={() => onRequestExtension(deliverable)}
                  >
                    Request Extension
                  </Button>
                )}
              </div>
            )}
        </div>
      </DropdownItem>
    </div>
  );
};

export default DeliverableSubmitted;
